// src/hooks/useTeamMembers.ts
//
// A tiny read hook for the reassign picker (Story 4.4). Reuses 1.4's
// teamService.listTeam() — the SAME query path the Settings → Team screen uses
// (org's admin + market_access_user profiles with position), never a second
// users query. Plain useState/useEffect per the repo's hook style (no React
// Query). The picker filters to `status === 'active'` client-side; the server's
// tx_reassign target_inactive check is the real boundary.

import { useState, useEffect } from 'react'
import { teamService } from '../services/teamService'
import type { YakTeamMember } from '../types/yak'

interface UseTeamMembersReturn {
  members: YakTeamMember[]
  loading: boolean
  error: string | null
}

export function useTeamMembers(): UseTeamMembersReturn {
  const [members, setMembers] = useState<YakTeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // loading starts true (initial state); the effect runs once on mount, so no
    // synchronous setState here — the async promise callbacks settle it.
    let active = true
    teamService
      .listTeam()
      .then(m => {
        if (!active) return
        setMembers(m)
        setError(null)
      })
      .catch(err => {
        if (!active) return
        setError(err instanceof Error ? err.message : 'Failed to load the team')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  return { members, loading, error }
}
